import * as Sentry from "@sentry/vue";
import Swal from "sweetalert2";

const MANUAL_REPORT_SHORTCUT = "Ctrl + Alt + B";

let sentryEnabled = false;
let manualReportInProgress = false;

async function copyText(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const input = document.createElement("textarea");
  input.value = text;
  input.setAttribute("readonly", "");
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.appendChild(input);
  input.select();
  document.execCommand("copy");
  document.body.removeChild(input);
}

function getCurrentUserContext() {
  try {
    const user = JSON.parse(localStorage.getItem("usuario"));

    if (!user) return null;

    return {
      id: String(user.id || user.usuario_id || user.login || "unknown"),
      accountId: user.conta_id || user.contaId || null,
      clientId: user.cliente_id || user.clienteId || null
    };
  } catch (error) {
    return null;
  }
}

function addUserContextToEvent(event) {
  const user = getCurrentUserContext();

  if (!user) return event;

  event.user = { ...event.user, id: user.id };
  event.tags = {
    ...event.tags,
    conta_id: user.accountId || "sem-conta",
    cliente_id: user.clientId || "sem-cliente"
  };

  return event;
}

export function initSentry(Vue, router) {
  const dsn = process.env.VUE_APP_SENTRY_DSN;

  if (!dsn) {
    console.warn(
      "Sentry desativado: configure VUE_APP_SENTRY_DSN para habilitar os relatos de bug."
    );
    return false;
  }

  Sentry.init({
    Vue,
    dsn,
    environment: process.env.VUE_APP_NODE_ENV || process.env.NODE_ENV,
    attachProps: false,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true
      })
    ],
    // Mantem somente um buffer local. Nada e enviado continuamente.
    replaysSessionSampleRate: 0,
    // Ao ocorrer um erro real, envia o buffer que antecedeu o erro.
    replaysOnErrorSampleRate: 1,
    tracesSampleRate: 0,
    beforeSend: addUserContextToEvent
  });

  router.afterEach((to, from) => {
    Sentry.addBreadcrumb({
      category: "navigation",
      type: "navigation",
      level: "info",
      message: `${from.fullPath || "inicio"} -> ${to.fullPath}`,
      data: {
        from: from.name || from.path || null,
        to: to.name || to.path || null
      }
    });
  });

  sentryEnabled = true;
  return true;
}

export async function captureManualBugReport(description, router) {
  if (!sentryEnabled) {
    throw new Error("Sentry nao esta configurado neste ambiente.");
  }

  const replay = Sentry.getReplay();
  const route = router.currentRoute;
  const reportContext = {
    description: description || "Sem descricao informada",
    routeName: route.name || null,
    routePath: route.fullPath,
    url: window.location.href,
    online: navigator.onLine,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen: `${window.screen.width}x${window.screen.height}`,
    replayId: replay ? replay.getReplayId() : null
  };

  Sentry.addBreadcrumb({
    category: "manual-report",
    level: "warning",
    message: "Usuario acionou o relato manual de bug",
    data: reportContext
  });

  const eventId = Sentry.captureMessage("Bug reportado manualmente", {
    level: "warning",
    tags: { origem: "atalho-manual" },
    contexts: { relato_manual: reportContext }
  });

  // Envia o ultimo minuto mantido pelo Replay e continua gravando a sessao.
  if (replay) await replay.flush();

  // Aguarda brevemente o envio do evento antes de confirmar para o usuario.
  await Sentry.flush(5000);

  return {
    eventId,
    replayId: replay ? replay.getReplayId() : null
  };
}

export function installManualBugReportShortcut(router) {
  window.addEventListener("keydown", async (event) => {
    const shortcutPressed =
      event.ctrlKey && event.altKey && event.key.toLowerCase() === "b";

    if (!shortcutPressed || manualReportInProgress) return;

    event.preventDefault();
    manualReportInProgress = true;

    try {
      const result = await Swal.fire({
        title: "Relatar bug",
        text: `Descreva o problema. Será enviado o último minuto de atividade. Atalho: ${MANUAL_REPORT_SHORTCUT}.`,
        input: "textarea",
        inputPlaceholder:
          "O que aconteceu e o que você esperava que acontecesse?",
        inputAttributes: { "aria-label": "Descrição do bug" },
        showCancelButton: true,
        confirmButtonText: "Enviar diagnóstico",
        cancelButtonText: "Cancelar",
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !Swal.isLoading(),
        preConfirm: async (description) => {
          try {
            return await captureManualBugReport(description, router);
          } catch (error) {
            Swal.showValidationMessage(
              `Não foi possível enviar: ${error.message}`
            );
            return false;
          }
        }
      });

      if (result.value) {
        const copyResult = await Swal.fire({
          icon: "success",
          title: "Diagnóstico enviado",
          text: "Copie o código gerado e compartilhe com o suporte para que ele encontre o diagnóstico.",
          input: "text",
          inputValue: result.value.eventId,
          inputAttributes: {
            readonly: "readonly",
            "aria-label": "Código do diagnóstico"
          },
          confirmButtonText: "Copiar código",
          showCancelButton: true,
          cancelButtonText: "Fechar"
        });

        if (copyResult.value) {
          await copyText(result.value.eventId);
          await Swal.fire({
            icon: "success",
            title: "Código copiado",
            text: "Agora você pode compartilhar o código com o suporte.",
            timer: 2500,
            showConfirmButton: false
          });
        }
      }
    } finally {
      manualReportInProgress = false;
    }
  });
}
